<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import Switch from 'next/switch/Switch.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Same bounds as AccountSettingsSchema: deleting is irreversible, so a
// period shorter than a week is refused, and a decade is the longest anyone
// can ask this app to hold on to a recording.
const MIN_RETENTION_DAYS = 7;
const MAX_RETENTION_DAYS = 3650;

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const isEnabled = ref(true);
const retentionDays = ref('');
const isSaving = ref(false);
// Set while the admin has typed a retention value that isn't saved yet, so a
// settings refresh triggered by toggling the switch doesn't wipe it out.
let retentionDirty = false;

watch(
  currentAccount,
  () => {
    const { call_recording_enabled, call_recording_retention_days } =
      currentAccount.value?.settings || {};
    // Unset means on: only an explicit false turns recording off.
    isEnabled.value = call_recording_enabled !== false;
    if (!retentionDirty) {
      retentionDays.value = call_recording_retention_days ?? '';
    }
  },
  { deep: true, immediate: true }
);

const retentionModel = computed({
  get: () => retentionDays.value,
  set: value => {
    retentionDirty = true;
    retentionDays.value = value;
  },
});

const saveSettings = async settings => {
  try {
    await updateAccount(settings, { silent: true });
    useAlert(t('GENERAL_SETTINGS.FORM.CALL_RECORDING.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.CALL_RECORDING.API.ERROR'));
    throw error;
  }
};

const toggleRecording = async () => {
  // The switch already flipped isEnabled via v-model, so its negation is
  // what it was a moment ago.
  const previousValue = !isEnabled.value;
  try {
    await saveSettings({ call_recording_enabled: isEnabled.value });
  } catch (error) {
    isEnabled.value = previousValue;
  }
};

// Empty keeps recordings forever; anything else is a whole number of days.
const saveRetention = async () => {
  const days = retentionDays.value === '' ? null : Number(retentionDays.value);
  if (
    days !== null &&
    !(
      Number.isInteger(days) &&
      days >= MIN_RETENTION_DAYS &&
      days <= MAX_RETENTION_DAYS
    )
  ) {
    useAlert(t('GENERAL_SETTINGS.FORM.CALL_RECORDING.RETENTION.ERROR'));
    return;
  }
  isSaving.value = true;
  try {
    await saveSettings({ call_recording_retention_days: days });
    retentionDirty = false;
  } catch (error) {
    // Already alerted inside saveSettings — keep the typed value so the
    // admin doesn't have to retype it to retry.
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.CALL_RECORDING.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.CALL_RECORDING.NOTE')"
    with-border
  >
    <template #headerActions>
      <div class="flex justify-end">
        <Switch v-model="isEnabled" @change="toggleRecording" />
      </div>
    </template>
    <form class="grid gap-4" @submit.prevent="saveRetention">
      <WithLabel
        name="call_recording_retention_days"
        :label="t('GENERAL_SETTINGS.FORM.CALL_RECORDING.RETENTION.LABEL')"
        :help-message="t('GENERAL_SETTINGS.FORM.CALL_RECORDING.RETENTION.HELP')"
      >
        <NextInput
          id="call_recording_retention_days"
          v-model="retentionModel"
          type="number"
          min="7"
          max="3650"
          class="w-full"
          :placeholder="
            t('GENERAL_SETTINGS.FORM.CALL_RECORDING.RETENTION.PLACEHOLDER')
          "
        />
      </WithLabel>
      <div>
        <NextButton
          blue
          type="submit"
          :is-loading="isSaving"
          :label="t('GENERAL_SETTINGS.FORM.CALL_RECORDING.RETENTION.SAVE')"
        />
      </div>
    </form>
  </SectionLayout>
</template>
